import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const InvestorProfileCreatePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    companyOrFund: '',
    investorTypes: [],
    developmentStages: [],
    investmentRounds: [],
    investmentSize: '',
    investmentThesis: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const investorTypeOptions = [
    'Seed', 'Angel', 'Early Stage VC', 'Late Stage VC', 'Accelerator'
  ];
  
  const developmentStageOptions = [
    'Concept', 'Preclinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'NDA'
  ];
  
  const investmentRoundOptions = [
    'Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Series D'
  ];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const handleCheckboxChange = (field, value) => {
    const updatedValues = formData[field].includes(value)
      ? formData[field].filter(item => item !== value)
      : [...formData[field], value];
    
    setFormData({ ...formData, [field]: updatedValues });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Insert new investor profile
      const { error: insertError } = await supabase
        .from('investor_profiles')
        .insert({
          user_id: user.id,
          company_or_fund: formData.companyOrFund,
          investor_types: formData.investorTypes,
          development_stages: formData.developmentStages,
          investment_rounds: formData.investmentRounds,
          investment_size: parseFloat(formData.investmentSize) || 0,
          investment_thesis: formData.investmentThesis
        });
      
      if (insertError) throw insertError;
      
      // Add investor type to user_types table
      const { error: typeError } = await supabase
        .from('user_types')
        .upsert({
          user_id: user.id,
          type: 'investor',
          is_active: true
        }, {
          onConflict: 'user_id,type',
          ignoreDuplicates: false
        });
      
      if (typeError) throw typeError;
      
      // For backward compatibility, also update the user_type field
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ user_type: 'investor' })
        .eq('user_id', user.id);
      
      if (updateError) throw updateError;
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error creating investor profile:', err);
      setError('Failed to create investor profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <LoadingScreen message="Creating your investor profile..." />;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Create Investor Profile</h1>
        
        {error && (
          <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="companyOrFund" className="block text-gray-700 font-medium mb-2">
                Company or Fund Name*
              </label>
              <input
                type="text"
                id="companyOrFund"
                name="companyOrFund"
                value={formData.companyOrFund}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Investor Type*
              </label>
              <div className="space-y-2">
                {investorTypeOptions.map((type) => (
                  <div key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`type-${type}`}
                      checked={formData.investorTypes.includes(type)}
                      onChange={() => handleCheckboxChange('investorTypes', type)}
                      className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor={`type-${type}`} className="ml-2 block text-gray-700">
                      {type}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Development Stage of Interest*
              </label>
              <div className="space-y-2">
                {developmentStageOptions.map((stage) => (
                  <div key={stage} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`stage-${stage}`}
                      checked={formData.developmentStages.includes(stage)}
                      onChange={() => handleCheckboxChange('developmentStages', stage)}
                      className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor={`stage-${stage}`} className="ml-2 block text-gray-700">
                      {stage}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Investment Round*
              </label>
              <div className="space-y-2">
                {investmentRoundOptions.map((round) => (
                  <div key={round} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`round-${round}`}
                      checked={formData.investmentRounds.includes(round)}
                      onChange={() => handleCheckboxChange('investmentRounds', round)}
                      className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor={`round-${round}`} className="ml-2 block text-gray-700">
                      {round}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="investmentSize" className="block text-gray-700 font-medium mb-2">
                Investment Size (Million $USD)*
              </label>
              <input
                type="number"
                id="investmentSize"
                name="investmentSize"
                value={formData.investmentSize}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
                min="0"
                step="1"
              />
              <p className="text-sm text-gray-500 mt-1">Enter investment size in millions, e.g., 15 for $15 million</p>
            </div>
            
            <div className="mb-6">
              <label htmlFor="investmentThesis" className="block text-gray-700 font-medium mb-2">
                Investment Thesis*
              </label>
              <textarea
                id="investmentThesis"
                name="investmentThesis"
                value={formData.investmentThesis}
                onChange={handleInputChange}
                rows="4"
                className="form-textarea w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvestorProfileCreatePage; 